import { CrossCircledIcon } from "@radix-ui/react-icons";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import { cva, VariantProps } from "class-variance-authority";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

const bannerVariants = cva("rounded-lg px-4 py-3 text-sm  no-underline", {
  variants: {
    variant: {
      default:
        "bg-slatea-action border border-slatea-normal text-slatea-normal",
      warning:
        "bg-ambera-action border border-ambera-normal text-ambera-normal",
      destructive: "bg-reda-action border border-reda-normal text-reda-normal",
      success:
        "bg-greena-action border border-greena-normal text-greena-normal",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type BannerProps = VariantProps<typeof bannerVariants> &
  React.ComponentProps<"div">;

export const Banner = function Banner({
  variant,
  className,
  children,
  ...props
}: BannerProps) {
  return (
    <div {...props} className={cn(className, bannerVariants({ variant }))}>
      <div className="flex items-center justify-between gap-2">{children}</div>
    </div>
  );
};

export function ErrorBanner({ error }: { error: AxiosError | null }) {
  // Accepts null so callers can hand a mutation's `error` straight over. Every
  // call site already guarded with `error && <ErrorBanner .../>`, and in
  // create.simple.tsx that guard did not narrow — the mutation there declares
  // no `onError`, and TypeScript would not narrow `AxiosError | null` through
  // it even via a local binding. Handling null here is the smaller change and
  // the sturdier contract.
  //
  // The hooks stay above the null check: bailing out before them would make
  // them conditional, which breaks the render-order rule.
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!error) return;
    bannerRef.current?.scrollIntoView({ behavior: "smooth" });
    Sentry.captureException(error);
  }, [error]);

  if (!error) return null;

  let message = error.message;

  if (typeof error.response === "string") {
    message = error.response;
  }
  if (typeof error.response?.data === "string") {
    message = error.response?.data;
  } else if (
    (error.response?.data as undefined | { details?: { message: string }[] })
      ?.details
  ) {
    message =
      (error.response?.data as { details: { message: string }[] }).details?.[0]
        ?.message || "";
  } else if (
    error.response?.data &&
    "error" in (error.response?.data as object)
  ) {
    message = (error.response?.data as { error: string }).error;
  } else if (
    error.response?.data &&
    "detail" in (error.response?.data as object)
  ) {
    message = (error.response?.data as { detail: string }).detail;
  } else if (
    error.response?.data &&
    "message" in (error.response?.data as object)
  ) {
    // feed-processor's 420s -- empty or malformed manifest, a Bluesky record
    // the PDS rejected -- carry `{ message, issues: [{ error }] }`. Without
    // this branch the author read "Request failed with status code 420" and
    // the reason stayed in the network tab.
    const data = error.response.data as {
      message: string;
      issues?: { error?: string }[];
    };
    const issue = data.issues?.[0]?.error;
    message = issue && issue !== data.message ? `${data.message} ${issue}` : data.message;
  }

  return (
    <Banner ref={bannerRef} variant="destructive">
      <div className="flex gap-3 items-center">
        <CrossCircledIcon className="h-6 w-6" />
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">Oh no! Something went wrong.</span>
          {message && <span className="text-sm">{message}</span>}
        </div>
      </div>
    </Banner>
  );
}
